import { getInteractionName } from "@/common/helpers";
import Timer from "@/components/atoms/timer/Timer";
import {
	InteractionDto,
	InteractionHandlingStatus,
} from "@/repository/models";
import { Text } from "@fluentui/react-components";
import { ListItemProps } from "@/components/atoms/list";
import {
	getInteractionSecondaryInfo,
	getInteractionStatusText,
} from "@/common/helpers/supervisorHelper";
import { StatusCell } from "@/components/atoms/statusCell";
import { formatDate } from "@/common/helpers/dateHelper";
import { IntlShape } from "react-intl";
import { InteractionListIcon } from "@/components/molecules/interactionListIcon";
import { SubHeaderWithDirection } from "@/components/atoms/directionArrow";

export enum InteractionTimerType {
	LiveTimer = "timer",
	FormattedDate = "formattedDate",
	None = "none",
}

export interface InteractionMappingOptions {
	intl: IntlShape;
	interactionHandlingStatus: InteractionHandlingStatus;
	timerType?: InteractionTimerType;
	hideUnreadIcon?: boolean;
	onItemClick?: (interaction: InteractionDto) => void;
	showQueueName?: boolean;
}

export const mapInteractionsToListItems = (
	interactions: InteractionDto[] | undefined,
	options: InteractionMappingOptions
): ListItemProps[] => {
	const {
		intl,
		interactionHandlingStatus,
		timerType = InteractionTimerType.LiveTimer,
		hideUnreadIcon = true,
		onItemClick,
		showQueueName = false,
	} = options;

	if (!interactions) {
		return [];
	}

	return interactions.map((interaction: InteractionDto) => {
		const renderTimer = () => {
			if (interaction.isOnHold) {
				return (
					<Text>
						{intl.formatMessage({
							id: `Interaction.Operations.Parked`,
						})}
					</Text>
				);
			}

			switch (timerType) {
				case InteractionTimerType.LiveTimer:
					return (
						<StatusCell
							timer={<Timer startDate={interaction?.startDate} />}
							status={getInteractionStatusText(interaction.status, intl)}
						/>
					);

				case InteractionTimerType.FormattedDate:
					return (
						<StatusCell
							timer={
								<span>
									{formatDate(new Date(interaction.startDate), false, true)}
								</span>
							}
							status={getInteractionStatusText(interaction.status, intl)}
						/>
					);

				case InteractionTimerType.None:
				default:
					return null;
			}
		};

		const secondaryInfo = getInteractionSecondaryInfo(interaction, intl);

		return {
			key: interaction.id,
			header: getInteractionName(interaction.type, interaction.name, intl),
			subHeader: secondaryInfo ? (
				<SubHeaderWithDirection
					text={secondaryInfo}
					direction={interaction.direction}
				/>
			) : undefined,
			content: interaction.description,
			media: (
				<InteractionListIcon
					interaction={interaction}
					interactionHandlingStatus={interactionHandlingStatus}
					hideUnreadIcon={hideUnreadIcon}
					hideDirectionArrow
				/>
			),
			headerMedia: (
				<>
					{showQueueName && (
						<div className={"additionalContent"} title={interaction.queueName}>
							{interaction.queueName}
						</div>
					)}
					{renderTimer()}
				</>
			),
			onClick: onItemClick ? () => onItemClick(interaction) : undefined,
		};
	});
};
